import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import HubRounded from '@mui/icons-material/HubRounded';
import CompareArrowsRounded from '@mui/icons-material/CompareArrowsRounded';
import PictureAsPdfRounded from '@mui/icons-material/PictureAsPdfRounded';
import { BarList, EmptyState, MetricCard, Panel, type MetricTrend } from '../../../ui-kit';
import { fetchCqSpotlight, type CqDay, type CqSpotlight } from '../../../services/carrierQuotingService';
import AgentFlow, { type FlowNode } from '../rocco/AgentFlow';
import { useLive } from '../rocco/live';
import { BrandLogo } from './logos';
import { dollars } from './status';

const whole = (v: number) => v.toLocaleString();

/** What the page draws before the figures arrive: every part present, all empty. */
const EMPTY: CqSpotlight = {
  rater: '', rater_logo: '', carriers: [],
  flow: { last_run_at: null, error: null, reading_now: 0, waiting_for_you: 0, quoting_now: 0 },
  tiles: {
    quotes: [], ready: [],
    totals: {
      households: 0, proposals: 0, carriers_asked: 0, carrier_quotes: 0, average_saving: null, average_seconds: null,
    },
  },
  answers: [],
  won: [],
};

/**
 * The page Carrier Quoting opens on. Every figure is read from the quotes
 * that have actually been run.
 *
 * Top to bottom: how a household's paperwork becomes a proposal, the work in
 * numbers, then what the carriers have been saying.
 */
export default function SpotlightPage() {
  const navigate = useNavigate();
  const { data, error } = useLive(fetchCqSpotlight, 10_000);

  if (error && !data) return <EmptyState message={error} />;
  const loading = !data;
  const { tiles, rater, rater_logo: raterLogo, carriers } = data ?? EMPTY;
  const throughRater = carriers.filter((c) => c.route === 'rater').map((c) => c.name);
  const byWebsite = carriers.filter((c) => c.route === 'portal').map((c) => c.name);
  const totals = tiles.totals;

  const nodes: FlowNode[] = [
    {
      id: 'upload', step: 'Paperwork in', name: 'What the household sent',
      detail: 'Declarations page, application, licence photos',
      logo: <UploadFileRounded sx={{ fontSize: 20 }} />, to: 'new',
    },
    {
      id: 'file', step: 'Reads and checks', name: 'One customer file',
      detail: 'A person confirms it before it goes anywhere',
      logo: <FactCheckRounded sx={{ fontSize: 20 }} />, to: 'quotes',
    },
    {
      id: 'carriers', step: 'Asks the carriers', name: rater || 'Through the rater',
      detail: [throughRater.join(', '), byWebsite.length ? `${byWebsite.join(', ')} by its website` : '']
        .filter(Boolean).join(' · ') || "Under the agency's own carrier logins",
      logo: rater ? <BrandLogo id={raterLogo} name={rater} size={36} /> : <HubRounded sx={{ fontSize: 20 }} />,
      to: 'integrations',
    },
    {
      id: 'compare', step: 'Compares', name: 'Like for like',
      detail: 'And flags what the household should know',
      logo: <CompareArrowsRounded sx={{ fontSize: 20 }} />, to: 'configuration',
    },
    {
      id: 'proposal', step: 'Proposal', name: 'Ready to send',
      detail: "With the agency's name and logo",
      logo: <PictureAsPdfRounded sx={{ fontSize: 20 }} />, to: 'quotes',
    },
  ];

  const trend = (points: CqDay[], label: string): MetricTrend => ({
    points: points.map((p, i) => ({
      key: p.day,
      label: i === points.length - 1 ? 'Today'
        : new Date(`${p.day}T12:00:00Z`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }),
      value: p.value,
    })),
    formatValue: whole,
    label,
  });

  return (
    <Stack spacing={2}>
      <AgentFlow
        nodes={nodes}
        loading={loading}
        subtitle="From the paperwork landing to a proposal the customer can read"
        onOpen={(to) => navigate(`/agents/carrier_quoting/${to}`)}
      />

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' } }}>
        <MetricCard loading={loading} label="Households quoted by AI" value={whole(totals.households)}
          info="Households whose paperwork was read and put into a customer file."
          trend={trend(tiles.quotes, 'Quotes by day, this week')} />
        <MetricCard loading={loading} label="Proposals ready" value={whole(totals.proposals)}
          info="Quotes where the carriers answered and a proposal can be opened."
          trend={trend(tiles.ready, 'Proposals by day, this week')} />
        <MetricCard loading={loading} label="Carrier quotes"
          value={totals.carriers_asked ? `${whole(totals.carrier_quotes)} of ${whole(totals.carriers_asked)}` : '—'}
          info="Prices that came back, out of every carrier asked. The rest referred, declined or are waiting." />
        <MetricCard loading={loading} label="Average saving, same cover" value={dollars(totals.average_saving)}
          info="For the term, against what the household pays today, where it has a policy in force." />
        <MetricCard loading={loading} label="Confirmed to compared"
          value={totals.average_seconds != null ? `${totals.average_seconds}s` : '—'}
          info="From a person confirming the customer file to every carrier's answer lined up, on average." />
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'start' }}>
        <Panel title="What the carriers said" subtitle="Every carrier asked, so far" loading={loading} loadingHeight={200}>
          {data?.answers.some((r) => r.value) ? (
            <BarList
              rows={data.answers.map((row) => ({ key: row.label, label: row.label, value: row.value }))}
              formatValue={whole}
              valueLabel="Carriers"
              labelHeading="Answer"
            />
          ) : (
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              No carrier has been asked yet.
            </Typography>
          )}
        </Panel>
        <Panel title="Who we recommended" subtitle="The carrier on each proposal" loading={loading} loadingHeight={200}>
          {data?.won.length ? (
            <BarList
              rows={data.won.map((row) => ({ key: row.label, label: row.label, value: row.value }))}
              formatValue={whole}
              valueLabel="Proposals"
              labelHeading="Carrier"
            />
          ) : (
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              No proposal yet.
            </Typography>
          )}
        </Panel>
      </Box>
    </Stack>
  );
}
